import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

// 从菜单列表中提取权限
function extractPermissionsFromMenu(menuList) {
  const permissions = [];
  
  function traverseMenu(menu) {
    // 添加当前菜单的权限
    permissions.push({
      name: menu.name,
      label: menu.name,
      path: menu.path
    });
    
    // 递归处理子菜单
    if (menu.children && menu.children.length > 0) {
      menu.children.forEach(subMenu => traverseMenu(subMenu));
    }
  }
  
  menuList.forEach(menu => traverseMenu(menu));
  return permissions;
}

// 创建Vuex store
export default new Vuex.Store({
  // 状态
  state: {
    // 用户信息
    userInfo: {
      name: '',
      avatar: '',
      phone: '',
      role: '', // 用户角色
      permissions: [] // 用户权限列表
    },
    // 是否登录
    isLogin: false,
    // 权限列表
    permissionList: [],
    // 菜单列表
    menuList: []
  },
  
  // 改变状态的方法
  mutations: {
    // 设置用户信息
    setUserInfo(state, userInfo) {
      state.userInfo = userInfo;
      state.isLogin = true;
      // 保存用户信息到localStorage
      localStorage.setItem('userAccount', JSON.stringify(userInfo));
    },
    
    // 清除用户信息
    clearUserInfo(state) {
      state.userInfo = {
        name: '',
        avatar: '',
        phone: '',
        role: '',
        permissions: []
      };
      state.isLogin = false;
      // 清除localStorage中的用户信息
      localStorage.removeItem('userAccount');
    },
    
    // 更新用户头像
    updateUserAvatar(state, avatar) {
      state.userInfo.avatar = avatar;
      // 更新localStorage中的用户信息
      localStorage.setItem('userAccount', JSON.stringify(state.userInfo));
    },
    
    // 更新用户信息（头像和昵称）
    updateUserInfo(state, userInfo) {
      state.userInfo = { ...state.userInfo, ...userInfo };
      // 更新localStorage中的用户信息
      localStorage.setItem('userAccount', JSON.stringify(state.userInfo));
    },
    
    // 设置权限列表
    setPermissionList(state, permissions) {
      state.permissionList = permissions;
    },
    
    // 设置用户权限
    setUserPermissions(state, permissions) {
      state.userInfo.permissions = permissions;
      // 更新localStorage中的用户信息
      localStorage.setItem('userAccount', JSON.stringify(state.userInfo));
    },
    
    // 设置菜单列表
    setMenuList(state, menuList) {
      state.menuList = menuList;
    }
  },
  
  // 异步操作
  actions: {
    // 登录
    login({ commit }, loginData) {
      // 导入登录API
      return import('@/api/public.js').then(({ login }) => {
        return login(loginData).then(response => {
          // 检查响应状态
          if (response.code === 200) {
            // 登录成功，获取返回数据
            const { token, user, menu, permission } = response.data;
            
            // 将token存储到localStorage
            localStorage.setItem('token', token);
            
            // 处理用户信息
            const userInfo = {
              name: user.nickname || '管理员',
              avatar: user.avatar || '',
              phone: user.mobile,
              role: user.role,
              permissions: Object.keys(permission) // 将权限对象的键转换为数组
            };
            
            // 保存用户信息到store
            commit('setUserInfo', userInfo);
            // 设置用户权限
            commit('setUserPermissions', userInfo.permissions);
            // 处理菜单列表，转换为数组格式
            const menuList = Object.values(menu).map(item => ({
              path: `/${item.url || item.menu_id}`,
              name: item.name,
              icon: item.icon,
              isParent: item.children && item.children.length > 0,
              children: item.children || []
            }));
            // 设置菜单列表
            commit('setMenuList', menuList);
            // 保存菜单和权限到localStorage
            localStorage.setItem('menuList', JSON.stringify(menuList));
            localStorage.setItem('permissionList', JSON.stringify(permission));
            
            return response;
          } else {
            // 登录失败，抛出错误
            throw new Error(response.msg || '登录失败');
          }
        });
      });
    },
    
    // 退出登录
    logout({ commit }) {
      // 清除用户信息
      commit('clearUserInfo');
      // 清除菜单列表
      commit('setMenuList', []);
      // 清除token
      localStorage.removeItem('token');
      // 清除localStorage中的菜单和权限
      localStorage.removeItem('menuList');
      localStorage.removeItem('permissionList');
    },
    
    // 初始化用户信息
    initUserInfo({ commit }) {
      // 从localStorage中获取用户信息
      const userAccount = localStorage.getItem('userAccount');
      if (userAccount) {
        try {
          const userInfo = JSON.parse(userAccount);
          commit('setUserInfo', userInfo);
        } catch (error) {
          console.error('Failed to parse userAccount from localStorage:', error);
          localStorage.removeItem('userAccount');
        }
      }
      
      // 从localStorage中获取菜单列表
      const menuListStr = localStorage.getItem('menuList');
      if (menuListStr) {
        try {
          const menuList = JSON.parse(menuListStr);
          commit('setMenuList', menuList);
        } catch (error) {
          console.error('Failed to parse menuList from localStorage:', error);
          localStorage.removeItem('menuList');
        }
      }
      
      // 从localStorage中获取权限列表
      const permissionListStr = localStorage.getItem('permissionList');
      if (permissionListStr) {
        try {
          const permissionList = JSON.parse(permissionListStr);
          commit('setPermissionList', permissionList);
        } catch (error) {
          console.error('Failed to parse permissionList from localStorage:', error);
          localStorage.removeItem('permissionList');
        }
      }
    },
    
    // 加载权限列表
    loadPermissionList({ commit, state }) {
      // 如果用户已登录且有菜单列表，直接使用菜单列表生成权限
      if (state.isLogin && state.menuList.length > 0) {
        // 从菜单列表中提取权限
        const permissions = extractPermissionsFromMenu(state.menuList);
        commit('setPermissionList', permissions);
        return Promise.resolve(permissions);
      }
      
      // 模拟权限列表，实际项目中应该从API获取
      const mockPermissionList = [
        { name: 'home', label: '主页', path: '/home' },
        { name: 'system_admin', label: '管理员管理', path: '/system/admin' },
        { name: 'menu', label: '菜单管理', path: '/system/menu' },
        { name: 'permission', label: '权限管理', path: '/system/permission' },
        { name: 'role', label: '角色管理', path: '/system/role' },
        { name: 'tag', label: '标签管理', path: '/system/tag' },
        { name: 'group', label: '分组管理', path: '/system/group' },
        { name: 'device_management', label: '设备管理', path: '/device/list' },
        { name: 'temple', label: '寺庙管理', path: '/temple' },
        { name: 'scene', label: '场景管理', path: '/temple/hall' },
        { name: 'user_list', label: '用户列表', path: '/user' },
        { name: 'place_user_list', label: '场地用户列表', path: '/place_user' },
        { name: 'order', label: '订单管理', path: '/order' },
        { name: 'bless', label: '祈福管理', path: '/work/otherBless' },
        { name: 'mine', label: '个人中心', path: '/mine' },
        { name: 'message', label: '消息管理', path: '/message' },
        { name: 'customer', label: '客户管理', path: '/custom/list' },
        { name: 'qrcode', label: '二维码管理', path: '/qrcode' },
        { name: 'hall_dictionaries', label: '殿堂字典', path: '/dictionaries/hall' },
        { name: 'subscribe_list', label: '订阅列表', path: '/dictionaries/subscribe' },
        { name: 'customer_tag_list', label: '客户标签', path: '/dictionaries/customer' },
        { name: 'calendar', label: '日历管理', path: '/calendar' },
        { name: 'library', label: '素材库', path: '/library' },
        { name: 'tool_manager', label: '工具管理', path: '/tool/list' },
        { name: 'ExampleTable', label: '数据管理', path: '/table' },
        { name: 'ReportPage', label: '数据报表', path: '/report' },
        { name: 'task_projects', label: '项目管理', path: '/task/projects' },
        { name: 'task_boards', label: '任务看板', path: '/task/boards' }
      ];
      
      commit('setPermissionList', mockPermissionList);
      return Promise.resolve(mockPermissionList);
    }
  },
  
  // 计算属性
  getters: {
    // 获取用户信息
    getUserInfo: state => state.userInfo,
    // 获取是否登录
    getIsLogin: state => state.isLogin,
    // 获取用户名
    getUserName: state => state.userInfo.name,
    // 获取用户头像
    getUserAvatar: state => state.userInfo.avatar,
    // 获取用户角色
    getUserRole: state => state.userInfo.role,
    // 获取用户权限列表
    getUserPermissions: state => state.userInfo.permissions,
    // 获取权限列表
    getPermissionList: state => state.permissionList,
    // 检查用户是否有某个权限
    hasPermission: state => permission => {
      return state.userInfo.permissions.includes(permission);
    },
    // 检查用户是否有多个权限中的任意一个
    hasAnyPermission: state => permissions => {
      return permissions.some(permission => state.userInfo.permissions.includes(permission));
    },
    // 检查用户是否有全部权限
    hasAllPermissions: state => permissions => {
      return permissions.every(permission => state.userInfo.permissions.includes(permission));
    }
  }
});
